/**
 * useWsQueryUpdater — listens to WebSocket messages and updates TanStack Query cache.
 *
 * Maps incoming WsMessage types to their corresponding query keys and
 * updates the cache optimistically. This enables real-time UI updates
 * without polling.
 *
 * Requirements: 28.1, 28.2, 28.6
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getCognitoTokenSync } from '../lib/get-cognito-token';
import { useWebSocket } from './useWebSocket';
import { useAuthStore } from '../stores/auth-store';
import type { WsMessage, WsMessageType } from '../types/ws-message';

/**
 * Maps WsMessageType to the query key prefix that should be invalidated/updated.
 */
const MESSAGE_TO_QUERY_KEY: Record<WsMessageType, string[]> = {
  'deposit.captured': ['dashboard', 'caixa', 'events'],
  'alert.created': ['alerts', 'dashboard'],
  'alert.acknowledged': ['alerts', 'dashboard'],
  'alert.escalated': ['alerts', 'dashboard'],
  'alert.resolved': ['alerts', 'dashboard'],
  'device.status_changed': ['devices', 'dashboard', 'map'],
  'collection.status_changed': ['collections', 'caixa'],
  'collection.completed': ['collections', 'caixa', 'dashboard'],
  'dlq.updated': ['dashboard'],
  'report.ready': ['reports'],
  'notification.new': ['notifications'],
  'helpdesk.message': ['helpdesk'],
  'anomaly.detected': ['ai', 'dashboard'],
  'forecast.updated': ['ai', 'forecasts'],
  'maintenance.predicted': ['ai', 'maintenance'],
};

export interface UseWsQueryUpdaterOptions {
  /** WebSocket URL */
  url: string;
  /** Whether to enable the connection */
  enabled?: boolean;
}

/**
 * Hook that connects WebSocket messages to TanStack Query cache updates.
 *
 * On each incoming message:
 * 1. Verifies tenantId matches the current session
 * 2. Updates relevant query caches based on message type
 * 3. For alerts, increments/decrements the active alert count
 */
export function useWsQueryUpdater(options: UseWsQueryUpdaterOptions) {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.tenantId);

  const handleMessage = useCallback(
    (message: WsMessage) => {
      // Verify tenant scoping
      if (message.tenantId !== tenantId) return;

      const queryKeys = MESSAGE_TO_QUERY_KEY[message.type];

      // Invalidate all related query keys to trigger refetch
      for (const key of queryKeys) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }

      // Optimistic cache updates for specific message types
      if (message.type === 'alert.created') {
        queryClient.setQueryData<number>(['alerts', 'activeCount'], (prev) =>
          prev !== undefined ? prev + 1 : 1,
        );
      }

      if (message.type === 'alert.acknowledged') {
        queryClient.setQueryData<number>(['alerts', 'activeCount'], (prev) =>
          prev !== undefined && prev > 0 ? prev - 1 : 0,
        );
      }

      if (message.type === 'alert.resolved') {
        queryClient.setQueryData<number>(['alerts', 'activeCount'], (prev) =>
          prev !== undefined && prev > 0 ? prev - 1 : 0,
        );
      }

      if (message.type === 'notification.new') {
        queryClient.setQueryData<number>(['notifications', 'unreadCount'], (prev) =>
          prev !== undefined ? prev + 1 : 1,
        );
      }
    },
    [queryClient, tenantId],
  );

  const getToken = useCallback(() => {
    if (!useAuthStore.getState().isAuthenticated) return null;
    return getCognitoTokenSync();
  }, []);

  return useWebSocket({
    url: options.url,
    getToken,
    onMessage: handleMessage,
    enabled: options.enabled ?? true,
  });
}
